import React, {
  PropsWithChildren,
  createContext,
  startTransition,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Breakpoints as BreakpointsType } from 'types/theme'
import { breakpoints as themeBreakpoints } from 'styles/theme'

type BreakpointsBoolean = {
  [key in keyof BreakpointsType]: boolean
}
type BreakpointsState = BreakpointsBoolean & {
  activeBP: keyof BreakpointsType
}
type BreakpointsContextType = BreakpointsState

const DEFAULT_STATE: BreakpointsState = {
  base: true,
  xs: false,
  sm: false,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
  '3xl': false,
  '4xl': false,
  '5xl': false,
  activeBP: 'xs',
}

const BreakpointsContext = createContext<BreakpointsContextType>({
  ...DEFAULT_STATE,
})

const getBreakpoints = (): BreakpointsBoolean => {
  const keys = Object.keys(themeBreakpoints) as Array<keyof BreakpointsType>
  return keys.reduce((acc, key) => {
    acc[key] =
      typeof window === 'undefined' ||
      window.matchMedia(`(min-width: ${themeBreakpoints[key]}px)`).matches
    return acc
  }, {} as BreakpointsBoolean)
}

// identyczny z tym, co renderuje serwer (wszystko true)
const SSR_BREAKPOINTS = Object.keys(themeBreakpoints).reduce(
  (acc, key) => ({ ...acc, [key]: true }),
  {} as BreakpointsBoolean
)

export const BreakpointsProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [breakpoints, setBreakpoints] =
    useState<BreakpointsBoolean>(SSR_BREAKPOINTS)

  const activeBP = useMemo(
    () =>
      (
        Object.keys(breakpoints).slice().reverse() as Array<
          keyof BreakpointsBoolean
        >
      ).find((key) => breakpoints[key]) || 'xs',
    [breakpoints]
  )

  useEffect(() => {
    const listener = () => setBreakpoints(getBreakpoints())
    // A transition lets lazy Suspense boundaries finish hydrating first;
    // an urgent update would make React discard their server HTML
    startTransition(listener)
    window.addEventListener('resize', listener)
    return () => window.removeEventListener('resize', listener)
  }, [])

  const providerValues = useMemo(
    () => ({ ...breakpoints, activeBP }),
    [activeBP, breakpoints]
  )

  return (
    <BreakpointsContext.Provider value={providerValues}>
      {children}
    </BreakpointsContext.Provider>
  )
}

export default BreakpointsContext
